import MoralDamageSelect from '../components/MoralDamageSelect';
import { captureUnsavedFeedback } from '../utils/unsavedFeedback';
import MoneyInput from '../components/MoneyInput';
import { parseAmount, formatAmountInput } from '../utils/currencyInput';
import { calculateDamageAssessment } from '../utils/damageAssessment';
import React, { useState, useEffect, useRef } from 'react';
import { FaUser } from 'react-icons/fa';
import './Trattativa.css';

const calculateAge = (birthDate, referenceDate) => {
  const [birthYear, birthMonth, birthDay] = String(birthDate).split('T')[0].split('-').map(Number);
  const [year, month, day] = String(referenceDate).split('T')[0].split('-').map(Number);
  let age = year - birthYear;
  if (month < birthMonth || (month === birthMonth && day < birthDay)) {
    age--;
  }
  return age;
};
const formatVisitDate = value => value ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString('it-IT') : 'Data da indicare';

const euro = value => Number(value || 0).toLocaleString('it-IT', { useGrouping: 'always', minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Trattativa = ({ data = {}, setData, schedaId, clienteNome, operator, definizioneFisica = {}, onDefinitionSaved, onPracticeStatusChange, onTrattativaSaved }) => {
  const gridRef = useRef(null);
  const [definitionEditor, setDefinitionEditor] = useState(null);
  const [definitionBusy, setDefinitionBusy] = useState(false);
  const [definitionError, setDefinitionError] = useState('');
  const etaCalcolata = data.dataNascita && data.dataSinistro 
    ? calculateAge(data.dataNascita, data.dataSinistro) 
    : 0;
  const annoSinistro = Number(String(data.dataSinistro || '').slice(0, 4)) || new Date().getFullYear();
  const [annoParametri, setAnnoParametri] = useState(data.annoParametri || annoSinistro);
  const [anniDisponibili, setAnniDisponibili] = useState([]);
  const [parametri, setParametri] = useState({ anno: null, corrispondenza_esatta: true, valore_punto_micro: 947.30, valore_giorno_micro: 55.24, valore_punto_tun: 947.30, valore_giorno_tun: 55.24 });
  const [parametriError, setParametriError] = useState('');
  const annoSceltoManualmente = useRef(Boolean(data.annoParametri));

  useEffect(() => {
    if (data.annoParametri) {
      setAnnoParametri(data.annoParametri);
      annoSceltoManualmente.current = true;
    } else if (!annoSceltoManualmente.current && data.dataSinistro) {
      setAnnoParametri(annoSinistro);
    }
  }, [data.annoParametri, data.dataSinistro, annoSinistro]);

  useEffect(() => {
    fetch('/api/parametri-calcolo').then(response => response.ok ? response.json() : Promise.reject(new Error()))
      .then(rows => setAnniDisponibili(rows.map(row => row.anno))).catch(() => setAnniDisponibili([]));
  }, []);

  useEffect(() => {
    const year = Number(annoParametri);
    if (!Number.isSafeInteger(year) || year < 2000 || year > 2200) return;
    let active = true;
    setParametriError('');
    fetch(`/api/parametri-calcolo/anno/${year}`).then(async response => {
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Parametri non disponibili');
      return result;
    }).then(result => { if (active) setParametri(result); }).catch(cause => { if (active) setParametriError(cause.message); });
    return () => { active = false; };
  }, [annoParametri]);

  // Stati indipendenti per ogni box
  const [box1, setBox1] = useState(data.box1 || {
    dannoBiologico: 0,
    moraleBiologico: null,
    giorni100: 0,
    giorni75: 0,
    giorni50: 0,
    giorni25: 0,
    moraleTemporaneo: 0,
    speseMediche: 0,
    speseAccessorie: 0,
    mancatoGuadagno: 0,
    dannoMateriale: 0,
    risultato: null,
  });

  const [box2, setBox2] = useState(data.box2 || {
    dannoBiologico: 0,
    moraleBiologico: null,
    giorni100: 0,
    giorni75: 0,
    giorni50: 0,
    giorni25: 0,
    moraleTemporaneo: 0,
    speseMediche: 0,
    speseAccessorie: 0,
    mancatoGuadagno: 0,
    dannoMateriale: 0,
    risultato: null,
  });

  const [box3, setBox3] = useState(data.box3 || {
    dannoBiologico: 0,
    moraleBiologico: null,
    giorni100: 0,
    giorni75: 0,
    giorni50: 0,
    giorni25: 0,
    moraleTemporaneo: 0,
    speseMediche: 0,
    speseAccessorie: 0,
    mancatoGuadagno: 0,
    dannoMateriale: 0,
    definizione: 0,
    detrazioneInail: 0,
    acconto: 0,
    onorari: 0,
    risultato: null,
  });
  const lastEmittedData = useRef('');

  // I dati arrivano dopo l'apertura della pagina: riallinea le tre tabelle
  // senza riapplicare al componente l'eco dei suoi stessi aggiornamenti.
  useEffect(() => {
    const incomingSignature = JSON.stringify({ box1: data.box1, box2: data.box2, box3: data.box3, annoParametri: data.annoParametri });
    if (incomingSignature === lastEmittedData.current) return;
    if (data.box1) setBox1(previous => JSON.stringify(previous) === JSON.stringify(data.box1) ? previous : data.box1);
    if (data.box2) setBox2(previous => JSON.stringify(previous) === JSON.stringify(data.box2) ? previous : data.box2);
    if (data.box3) setBox3(previous => JSON.stringify(previous) === JSON.stringify(data.box3) ? previous : data.box3);
  }, [data.box1, data.box2, data.box3, data.annoParametri]);
  useEffect(() => {
    const suggested = parseAmount(data.speseMedicheSostenute);
    if (suggested <= 0) return;
    setBox1(previous => parseAmount(previous.speseMediche) > 0 ? previous : ({ ...previous, speseMediche: suggested.toFixed(2) }));
  }, [data.speseMedicheSostenute]);
  useEffect(() => {
    if (!definizioneFisica?.importo) return;
    setBox3(previous => parseAmount(previous.definizione) > 0 ? previous : ({ ...previous, definizione: definizioneFisica.importo, detrazioneInail: definizioneFisica.detrazioneInail || 0, acconto: definizioneFisica.acconto || 0, onorari: definizioneFisica.onorari || 0 }));
  }, [definizioneFisica]);

  // I valori della trattativa già salvati prevalgono sui suggerimenti automatici.
  const [campiModificati, setCampiModificati] = useState(() => {
    const modificati = new Set();
    const campi = [
      'giorni100', 'giorni75', 'giorni50', 'giorni25',
      'moraleTemporaneo', 'speseMediche', 'speseAccessorie',
      'mancatoGuadagno', 'dannoMateriale', 'moraleBiologico', 'dannoBiologico'
    ];
    if (data.box3) {
      campi.forEach((campo) => {
        const valoreSalvato = data.box3[campo];
        if (valoreSalvato != null &&
            String(valoreSalvato) !== String(data.box1?.[campo])) {
          modificati.add(campo);
        }
      });
    }
    return modificati;
  });
  const mounted = useRef(false);

  const focusGridField = (box, row) => {
    const field = gridRef.current?.querySelector(`[data-grid-box="${box}"][data-grid-row="${row}"]`);
    if (!field) return false;
    field.focus();
    if (field.tagName === 'INPUT') field.select();
    return true;
  };

  const handleGridKeyDown = event => {
    const field = event.target.closest('[data-grid-box][data-grid-row]');
    if (!field || !gridRef.current?.contains(field)) return;
    const box = Number(field.dataset.gridBox);
    const row = Number(field.dataset.gridRow);
    let nextBox = box;
    let nextRow = row;
    if (event.key === 'ArrowUp') nextRow -= 1;
    else if (event.key === 'ArrowDown' || event.key === 'Enter') nextRow += 1;
    else if (event.key === 'ArrowLeft') nextBox -= 1;
    else if (event.key === 'ArrowRight') nextBox += 1;
    else return;

    event.preventDefault();
    const maxRow = box === 2 ? 14 : 10;
    if ((event.key === 'ArrowDown' || event.key === 'Enter') && nextRow > maxRow) {
      nextBox += 1;
      nextRow = 0;
    } else if (event.key === 'ArrowUp' && nextRow < 0) {
      nextBox -= 1;
      nextRow = 10;
    }
    focusGridField(nextBox, nextRow);
  };

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (setData) {  // Opzionale: verifica se setData esiste
      const nextData = { box1, box2, box3, annoParametri: Number(annoParametri) || null };
      lastEmittedData.current = JSON.stringify(nextData);
      setData(nextData);
    }
  }, [box1, box2, box3, annoParametri, setData]);

  const calcolaRisultato = (box, eta) => calculateDamageAssessment(box, eta, parametri, data.responsabilitaControparte);

  // Effetto per calcolare i risultati automaticamente
  useEffect(() => {
    if (data.dataNascita && data.dataSinistro) {
      const etaCalcolata = calculateAge(data.dataNascita, data.dataSinistro);
      const withResult = (previous) => {
        const risultato = calcolaRisultato(previous, etaCalcolata);
        return JSON.stringify(previous.risultato) === JSON.stringify(risultato)
          ? previous : { ...previous, risultato };
      };
      setBox1(withResult);
      setBox2(withResult);
      setBox3(withResult);
    }
  }, [data.dataNascita, data.dataSinistro, data.responsabilitaControparte, box1, box2, box3, parametri]);

  // Funzione per aggiornare i valori del Box 3 quando Box 1 e Box 2 sono uguali
  useEffect(() => {
    setBox3(previous => {
      const nuovoBox3 = { ...previous };
  
      // Lista di tutti i campi da confrontare
      const campiDaConfrontare = [
        'giorni100', 'giorni75', 'giorni50', 'giorni25',
        'moraleTemporaneo', 'speseMediche', 'speseAccessorie', 'mancatoGuadagno', 'dannoMateriale'
      ];
  
      campiDaConfrontare.forEach((campo) => {
        // Gli importi possono avere formati diversi (100, 100.00 o 100,00),
        // ma rappresentare lo stesso valore.
        if (!campiModificati.has(campo) && parseAmount(box1[campo]) === parseAmount(box2[campo])) {
          nuovoBox3[campo] = box1[campo];
        }
      });
  
      // Gestione del campo moraleBiologico
      if (!campiModificati.has('moraleBiologico') && box1.moraleBiologico === box2.moraleBiologico) {
        nuovoBox3.moraleBiologico = box1.moraleBiologico;
      }
  
      // Non sovrascrivere il dannoBiologico se è già stato calcolato o modificato manualmente
      if (!campiModificati.has('dannoBiologico') && parseAmount(box1.dannoBiologico) === parseAmount(box2.dannoBiologico)) {
        nuovoBox3.dannoBiologico = box1.dannoBiologico;
      }
      return JSON.stringify(nuovoBox3) === JSON.stringify(previous) ? previous : nuovoBox3;
    });
  }, [box1, box2, campiModificati]);

  const openDefinition = () => {
    setDefinitionError('');
    if (!(parseAmount(box3.definizione) > 0)) { setDefinitionError('Inserisci prima la definizione nella tabella Trattativa'); return; }
    setDefinitionEditor({ data: new Date().toISOString().slice(0, 10), note: '' });
  };
  const saveDefinition = async event => {
    const commitFeedback = captureUnsavedFeedback(event.currentTarget); 
    event.preventDefault();
    if (!schedaId || definitionBusy) return;
    setDefinitionBusy(true); setDefinitionError('');
    try {
      const boxes = [['box1', box1], ['box2', box2], ['box3', box3]].map(([type, box]) => ({
        box_type: type, danno_biologico: Number(box.dannoBiologico || 0), morale_biologico: box.moraleBiologico || null,
        giorni_100: Number(box.giorni100 || 0), giorni_75: Number(box.giorni75 || 0), giorni_50: Number(box.giorni50 || 0), giorni_25: Number(box.giorni25 || 0),
        morale_temporaneo: Number(box.moraleTemporaneo || 0), spese_mediche: parseAmount(box.speseMediche), spese_accessorie: parseAmount(box.speseAccessorie),
        mancato_guadagno: parseAmount(box.mancatoGuadagno), danno_materiale: parseAmount(box.dannoMateriale), eta: etaCalcolata, anno_parametri: Number(annoParametri) || null,
        policy_data: type === 'box3' ? { settlement: { definizione: parseAmount(box3.definizione), detrazioneInail: parseAmount(box3.detrazioneInail), acconto: parseAmount(box3.acconto), onorari: parseAmount(box3.onorari) } } : { assessmentEntered: box.assessmentEntered === true }
      }));
      const tableResponse = await fetch(`/api/trattativa/scheda/${schedaId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ boxes }) });
      if (!tableResponse.ok) throw new Error((await tableResponse.json().catch(() => ({}))).error || 'Tabella Trattativa non salvata');
      const response = await fetch(`/api/workflow/schede/${schedaId}/definizione-fisica`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...definitionEditor, importo: box3.definizione, detrazioneInail: box3.detrazioneInail, acconto: box3.acconto, onorari: box3.onorari, operatore: operator?.nome || operator?.username || '', parametri: {
          anno: Number(annoParametri) || null,
          invaliditaPermanente: box3.dannoBiologico || 0,
          giorni100: box3.giorni100 || 0, giorni75: box3.giorni75 || 0, giorni50: box3.giorni50 || 0, giorni25: box3.giorni25 || 0,
          speseMediche: parseAmount(box3.speseMediche), responsabilitaControparte: box3.risultato?.responsabilitaControparte ?? null
        } })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Definizione non salvata');
      commitFeedback();
      onDefinitionSaved?.(result.definizioneFisica);
      onPracticeStatusChange?.(result.stato);
      onTrattativaSaved?.();
      setDefinitionEditor(null);
    } catch (error) { setDefinitionError(error.message); } finally { setDefinitionBusy(false); }
  };
  return (
    <div className="container trattativa-view">
      <h1>Calcolo Risarcimento Danno Biologico e Temporaneo</h1>
      <div className="calculation-year-selector">
        <div><span>PARAMETRI ECONOMICI</span><strong>Anno della tabella applicata</strong>
          <small>Proposto dall’anno del sinistro; puoi scegliere l’ultimo aggiornamento disponibile.</small></div>
        <label>Anno
          <input type="number" min="2000" max="2200" list="anni-parametri-disponibili" value={annoParametri}
            onChange={event => { annoSceltoManualmente.current = true; setAnnoParametri(event.target.value); }} />
          <datalist id="anni-parametri-disponibili">{anniDisponibili.map(year => <option key={year} value={year} />)}</datalist>
        </label>
        <div className="calculation-year-status"><b>Valori {parametri.anno || '—'}</b>
          {parametri.coefficienti_eta && <span>Coefficienti per età TUN: {parametri.anno}</span>}
          <span>Valore punto: € {Number(parametri.valore_punto_micro).toLocaleString('it-IT',{ useGrouping: 'always',minimumFractionDigits:2})}</span>
          <span>Giorno al 100%: € {Number(parametri.valore_giorno_micro).toLocaleString('it-IT',{ useGrouping: 'always',minimumFractionDigits:2})}</span>
          {parametri.anno && !parametri.corrispondenza_esatta && <em>Anno {annoParametri} non presente: applicati i valori {parametri.anno}.</em>}
          {parametriError && <em>{parametriError}</em>}
        </div>
      </div>
      <div className="eta-badge-container">
        {data?.dataNascita && data?.dataSinistro && (
          <div className="eta-auto-badge">
            <FaUser /> Età al sinistro: {calculateAge(data.dataNascita, data.dataSinistro)} anni
    </div>
  )}
</div>
      {/* Posiziona il badge qui (sopra il Box 1, fuori dai box) */}
      <div className="eta-badge" style={{ 
      position: 'absolute',
      top: '10px',
      left: '10px',
      zIndex: 10
      }}>
      {data.eta || '--'}
      </div>

      <div className="trattativa-container" ref={gridRef} onKeyDown={handleGridKeyDown}>
        <AssessmentTable
          title="Relazione di Parte"
          tone="parte"
          stato={box1}
          setStato={setBox1}
          eta={etaCalcolata}
          ageCoefficients={parametri.coefficienti_eta}
          dailyAmount={Number(parametri.valore_giorno_micro)}
          data={data}
          boxIndex={0}
        />
        <AssessmentTable
          title="Relazione di Controparte"
          tone="controparte"
          stato={box2}
          setStato={setBox2}
          eta={etaCalcolata}
          ageCoefficients={parametri.coefficienti_eta}
          dailyAmount={Number(parametri.valore_giorno_micro)}
          data={data}
          boxIndex={1}
        />
        <AssessmentTable
          title="Trattativa"
          tone="accordo"
          stato={box3}
          setStato={setBox3}
          eta={etaCalcolata}
          ageCoefficients={parametri.coefficienti_eta}
          dailyAmount={Number(parametri.valore_giorno_micro)}
          campiModificati={campiModificati}
          setCampiModificati={setCampiModificati}
          boxIndex={2}
        />
      </div>
      {schedaId && <section className={`physical-settlement-card ${definizioneFisica?.importo ? 'registered' : ''}`}>
        {definizioneFisica?.importo ? <>
          <div><span>Definizione del danno fisico</span><strong>Danno concordato a € {euro(definizioneFisica.importo)} · Saldo € {euro(definizioneFisica.saldo)}</strong><small>Beneficiario: {definizioneFisica.beneficiario || clienteNome || 'Cliente'} · {formatVisitDate(definizioneFisica.data)}{Number(definizioneFisica.onorari || 0) > 0 ? ` · Onorari € ${euro(definizioneFisica.onorari)}` : ''}</small></div>
          <button type="button" onClick={openDefinition}>Modifica definizione</button>
        </> : <>
          <div><span>Chiusura economica</span><strong>Conferma i valori della tabella Trattativa</strong><small>Definizione, detrazioni, saldo e onorari devono essere compilati nella tabella.</small></div>
          <button type="button" onClick={openDefinition} disabled={parseAmount(box3.definizione) <= 0} title={parseAmount(box3.definizione) <= 0 ? 'Inserisci prima la definizione nella tabella Trattativa' : ''}>Registra definizione</button>
        </>}
      </section>}
      {definitionEditor && <div className="definition-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && setDefinitionEditor(null)}><form className="definition-modal" onSubmit={saveDefinition}>
        <header><div><span>DEFINIZIONE DANNO FISICO</span><h2>Conferma la definizione</h2></div><button type="button" onClick={() => setDefinitionEditor(null)}>×</button></header>
        <div className="definition-confirmation">
          <div><span>Totale spettante</span><strong>€ {euro(box3.risultato?.totaleSpettante || 0)}</strong></div><div><span>Definizione</span><strong>€ {euro(parseAmount(box3.definizione))}</strong></div><div className="deduction"><span>Detrazione INAIL</span><strong>− € {euro(parseAmount(box3.detrazioneInail))}</strong></div><div><span>Acconto</span><strong>− € {euro(parseAmount(box3.acconto))}</strong></div><div className="balance"><span>Saldo</span><strong>€ {euro(Math.max(0, parseAmount(box3.definizione) - parseAmount(box3.detrazioneInail) - parseAmount(box3.acconto)))}</strong></div><div><span>Onorari</span><strong>€ {euro(parseAmount(box3.onorari))}</strong></div>
        </div><div className="definition-grid">
          <label><span>Data accordo</span><input required type="date" value={definitionEditor.data} onChange={event => setDefinitionEditor(previous => ({ ...previous, data: event.target.value }))} /></label>
          <label><span>Beneficiario</span><input readOnly value={clienteNome || 'Cliente'} /></label>
          <label className="wide"><span>Nota facoltativa</span><textarea rows="3" value={definitionEditor.note} onChange={event => setDefinitionEditor(previous => ({ ...previous, note: event.target.value }))} /></label>
        </div>
        {definitionError && <p className="definition-error">{definitionError}</p>}
        <footer><button type="button" onClick={() => setDefinitionEditor(null)}>Annulla</button><button type="submit" className="primary" disabled={definitionBusy}>{definitionBusy ? 'Salvataggio…' : 'Conferma definizione'}</button></footer>
      </form></div>}
    </div>
  );
};

// Componente Box riutilizzabile
export const AssessmentTable = ({ title, tone, stato, setStato, eta, ageCoefficients, dailyAmount, campiModificati, setCampiModificati, data, boxIndex, differentialMode = false }) => {
  const visitKeys = tone === 'parte' ? ['parteMedicoLegale', 'parteSpecialista'] : tone === 'controparte' ? ['controparteMedicoLegale', 'controparteSpecialista'] : [];
  const visitLabels = { parteMedicoLegale: 'Medico legale', parteSpecialista: 'Specialista', controparteMedicoLegale: 'Medico legale', controparteSpecialista: 'Specialista' };
  const visits = visitKeys.map(key => ({ key, ...(data?.visiteMedicoLegali?.[key] || {}) })).filter(visit => visit.medico || visit.data);
  const handleChange = (field, value) => {
    // Se setCampiModificati è definito, aggiungi il campo al Set dei campi modificati
    if (setCampiModificati) {
      setCampiModificati((prev) => new Set(prev).add(field));
    }
    // Aggiorna lo stato del box
    setStato((prev) => ({ ...prev, [field]: value, assessmentEntered: true }));
  };

  const handleBlur = (field, value) => {
    handleChange(field, formatAmountInput(value));
  };
  const gridField = row => ({
    'data-grid-box': boxIndex,
    'data-grid-row': row,
    onFocus: event => {
      if (event.currentTarget.tagName === 'INPUT') event.currentTarget.select();
    }
  });
  const handleWholeNumberChange = (field, value) => handleChange(field, value === '' ? '' : Number(value));
  const handleWholeNumberBlur = (field, value) => {
    if (value === '') handleChange(field, 0);
  };
  const definitionBalance = Math.max(0, parseAmount(stato.definizione) - parseAmount(stato.detrazioneInail) - parseAmount(stato.acconto));

  return (
    <div className={`trattativa-box trattativa-box--${tone}`}>
      <div className="trattativa-header">
        <span className="trattativa-accent" aria-hidden="true" />
        <h2>{title}</h2>
      </div>
      <div className={`trattativa-medical-visits ${visits.length ? '' : 'empty'}`} aria-hidden={visits.length ? undefined : 'true'}>
        {visits.map(visit => <div key={visit.key}><span>{visitLabels[visit.key]}</span><strong>{visit.medico || 'Medico da indicare'}</strong><small>{formatVisitDate(visit.data)}{visit.ora ? ` · ${visit.ora}` : ''}{visit.descrizione ? ` · ${visit.descrizione}` : ''}</small></div>)}
      </div>
      <table>
        <tbody>
          <tr className="trattativa-group"><th colSpan="3">Danno permanente</th></tr>
          {/* Danno Biologico */}
          <tr>
            <td>{differentialMode ? "Invalidità complessiva dopo l’evento" : "Danno Biologico"}</td>
            <td>
              <span className="assessment-input-unit"><input
                {...gridField(0)}
                type="number"
                step="0.5"
                aria-label={`Danno biologico ${title}`}
                title="Sono ammessi i mezzi punti, ad esempio 5,5"
                value={stato.dannoBiologico}
                onChange={(e) => handleWholeNumberChange('dannoBiologico', e.target.value)}
                onBlur={(e) => handleWholeNumberBlur('dannoBiologico', e.target.value)}
                min="0"
                max="100"
              /><span aria-hidden="true">%</span></span>
            </td>
            <td>€ {formatAmountInput(differentialMode ? stato.risultato?.differential?.after : stato.risultato?.dannoBiologico)}</td>
          </tr>

          {differentialMode&&<><tr><td>Biologico preesistente</td><td className="calculation-preexisting-value"><span className="assessment-input-unit"><input type="number" readOnly aria-label="Biologico preesistente nel riepilogo" value={stato.risultato?.differential?.preexisting ?? ''}/><span aria-hidden="true">%</span></span></td><td>− € {formatAmountInput(stato.risultato?.differential?.before)}</td></tr><tr><td><strong>Danno biologico differenziale</strong></td><td></td><td><strong>€ {formatAmountInput(stato.risultato?.dannoBiologico)}</strong></td></tr></>}
          {/* Danno Morale su Biologico */}
          <tr>
            <td>{differentialMode ? "Danno morale sul biologico differenziale" : "Danno Morale su Biologico"}</td>
            <td>
              <MoralDamageSelect
                {...gridField(1)}
                aria-label={`Danno morale ${title}`}
                points={stato.dannoBiologico}
                age={eta}
                ageCoefficients={ageCoefficients}
                value={stato.moraleBiologico}
                onChange={(e) => handleChange('moraleBiologico', e.target.value || null)}
              />
            </td>
            <td>€ {formatAmountInput(stato.risultato?.dannoMoraleBiologico)}</td>
          </tr>

          {/* Totale Permanente */}
          <tr>
            <td>Totale Permanente</td>
            <td></td>
            <td>
              € {formatAmountInput(
                (stato.risultato?.dannoBiologico || 0) +
                (stato.risultato?.dannoMoraleBiologico || 0)
              )}
            </td>
          </tr>

          {/* Giorni al 100% */}
          <tr className="trattativa-group"><th colSpan="3">Danno temporaneo</th></tr>
          <tr>
            <td>Giorni al 100%</td>
            <td>
              <input
                {...gridField(2)}
                type="number"
                value={stato.giorni100}
                onChange={(e) => handleWholeNumberChange('giorni100', e.target.value)}
                onBlur={(e) => handleWholeNumberBlur('giorni100', e.target.value)}
                min="0"
              />
            </td>
            <td>€ {formatAmountInput(stato.giorni100 * dailyAmount * 1)}</td>
          </tr>

          {/* Giorni al 75% */}
          <tr>
            <td>Giorni al 75%</td>
            <td>
              <input
                {...gridField(3)}
                type="number"
                value={stato.giorni75}
                onChange={(e) => handleWholeNumberChange('giorni75', e.target.value)}
                onBlur={(e) => handleWholeNumberBlur('giorni75', e.target.value)}
                min="0"
              />
            </td>
            <td>€ {formatAmountInput(stato.giorni75 * dailyAmount * 0.75)}</td>
          </tr>

          {/* Giorni al 50% */}
          <tr>
            <td>Giorni al 50%</td>
            <td>
              <input
                {...gridField(4)}
                type="number"
                value={stato.giorni50}
                onChange={(e) => handleWholeNumberChange('giorni50', e.target.value)}
                onBlur={(e) => handleWholeNumberBlur('giorni50', e.target.value)}
                min="0"
              />
            </td>
            <td>€ {formatAmountInput(stato.giorni50 * dailyAmount * 0.50)}</td>
          </tr>

          {/* Giorni al 25% */}
          <tr>
            <td>Giorni al 25%</td>
            <td>
              <input
                {...gridField(5)}
                type="number"
                value={stato.giorni25}
                onChange={(e) => handleWholeNumberChange('giorni25', e.target.value)}
                onBlur={(e) => handleWholeNumberBlur('giorni25', e.target.value)}
                min="0"
              />
            </td>
            <td>€ {formatAmountInput(stato.giorni25 * dailyAmount * 0.25)}</td>
          </tr>

          {/* Totale Danno Temporaneo */}
          <tr>
            <td>Totale Danno Temporaneo</td>
            <td></td>
            <td>€ {formatAmountInput(stato.risultato?.dannoTemporaneo)}</td>
          </tr>

          {/* Danno Morale su Temporaneo */}
          <tr>
            <td>Danno Morale su Temporaneo</td>
            <td>
              <span className="assessment-input-unit"><input
                {...gridField(6)}
                type="number"
                aria-label={`Danno morale su temporaneo ${title}`}
                value={stato.moraleTemporaneo}
                onChange={(e) => handleWholeNumberChange('moraleTemporaneo', e.target.value)}
                onBlur={(e) => handleWholeNumberBlur('moraleTemporaneo', e.target.value)}
                min="0"
                max="60"
              /><span aria-hidden="true">%</span></span>
            </td>
            <td>€ {formatAmountInput(stato.risultato?.dannoMoraleTemporaneo)}</td>
          </tr>

          {/* Spese Mediche - Spostato nella terza colonna */}
          <tr className="trattativa-group"><th colSpan="3">Spese e totale</th></tr>
          <tr>
            <td>Spese Mediche</td>
            <td></td>
            <td>
              <MoneyInput
                {...gridField(7)}
                type="text" inputMode="decimal"
                value={stato.speseMediche}
                onChange={(e) => handleChange('speseMediche', e.target.value)}
                onBlur={(e) => handleBlur('speseMediche', e.target.value)}
                min="0"
                step="0.01"
              />
            </td>
          </tr>

          {/* Spese Accessorie - Spostato nella terza colonna */}
          <tr>
            <td>Spese Accessorie</td>
            <td></td>
            <td>
              <MoneyInput
                {...gridField(8)}
                type="text" inputMode="decimal"
                value={stato.speseAccessorie}
                onChange={(e) => handleChange('speseAccessorie', e.target.value)}
                onBlur={(e) => handleBlur('speseAccessorie', e.target.value)}
                min="0"
                step="0.01"
              />
            </td>
          </tr>

          {/* Mancato Guadagno - Spostato nella terza colonna */}
          <tr>
            <td>Mancato Guadagno</td>
            <td></td>
            <td>
              <MoneyInput
                {...gridField(9)}
                type="text" inputMode="decimal"
                value={stato.mancatoGuadagno}
                onChange={(e) => handleChange('mancatoGuadagno', e.target.value)}
                onBlur={(e) => handleBlur('mancatoGuadagno', e.target.value)}
                min="0"
                step="0.01"
              />
            </td>
          </tr>

          {/* Danno Materiale - Spostato nella terza colonna */}
          <tr>
            <td>Danno Materiale</td>
            <td></td>
            <td>
              <MoneyInput
                {...gridField(10)}
                type="text" inputMode="decimal"
                value={stato.dannoMateriale}
                onChange={(e) => handleChange('dannoMateriale', e.target.value)}
                onBlur={(e) => handleBlur('dannoMateriale', e.target.value)}
                min="0"
                step="0.01"
              />
            </td>
          </tr>

          {/* Totale Complessivo */}
          <tr>
            <td>Totale Complessivo</td>
            <td></td>
            <td>€ {formatAmountInput(stato.risultato?.totale)}</td>
          </tr>
          <tr className={stato.risultato?.responsabilitaControparte == null ? 'responsibility-missing' : ''}>
            <td>Responsabilità controparte</td>
            <td></td>
            <td>{stato.risultato?.responsabilitaControparte == null ? 'Da definire' : `${stato.risultato.responsabilitaControparte}%`}</td>
          </tr>
          <tr className="payable-total-row">
            <td>Totale spettante</td>
            <td></td>
            <td>{stato.risultato?.totaleSpettante == null ? '—' : `€ ${formatAmountInput(stato.risultato.totaleSpettante)}`}</td>
          </tr>
          {tone === 'accordo' && <>
            <tr className="trattativa-group settlement-group"><th colSpan="3">Definizione economica</th></tr>
            <tr className="settlement-input-row"><td>Definizione</td><td></td><td><MoneyInput {...gridField(11)} type="text" inputMode="decimal" value={stato.definizione ?? ''} onChange={event => handleChange('definizione', event.target.value)} onBlur={event => handleBlur('definizione', event.target.value)} /></td></tr>
            <tr className="settlement-input-row deduction-row"><td>Detrazione INAIL</td><td></td><td><span className="negative-sign">−</span><MoneyInput {...gridField(12)} type="text" inputMode="decimal" value={stato.detrazioneInail ?? ''} onChange={event => handleChange('detrazioneInail', event.target.value)} onBlur={event => handleBlur('detrazioneInail', event.target.value)} /></td></tr>
            <tr className="settlement-input-row"><td>Acconto</td><td></td><td><span className="negative-sign">−</span><MoneyInput {...gridField(13)} type="text" inputMode="decimal" value={stato.acconto ?? ''} onChange={event => handleChange('acconto', event.target.value)} onBlur={event => handleBlur('acconto', event.target.value)} /></td></tr>
            <tr className="settlement-balance-row"><td>Saldo</td><td></td><td>€ {euro(definitionBalance)}</td></tr>
            <tr className="trattativa-group honoraria-group"><th colSpan="3">Onorari</th></tr>
            <tr className="settlement-input-row honoraria-row"><td>Onorari concordati</td><td></td><td><MoneyInput {...gridField(14)} type="text" inputMode="decimal" value={stato.onorari ?? ''} onChange={event => handleChange('onorari', event.target.value)} onBlur={event => handleBlur('onorari', event.target.value)} /></td></tr>
          </>}
        </tbody>
      </table>
    </div>
  );
};

export default Trattativa;

